/**
 * 원불교 법령 관리 시스템 - 메인 애플리케이션
 * 
 * 이 파일은 React 애플리케이션의 진입점입니다.
 * 라우팅 구조, 인증 시스템, 레이아웃을 정의합니다.
 * 
 * 주요 기능:
 * - 인증 시스템 (로그인/회원가입)
 * - 권한 기반 라우팅 (일반 사용자/관리자)
 * - 법령, 공지사항, 입법예고, 간행물 관리
 * - 사용자 프로필 및 관리자 기능
 * 
 * @author 원불교 법령 관리 시스템 개발팀
 * @version 1.0.0
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import koKR from 'antd/locale/ko_KR';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';

// i18n 설정 import
import './i18n';

// 페이지 컴포넌트들
import Home from './components/Home';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import Profile from './components/auth/Profile';
import MainLayout from './components/layout/MainLayout';
import LawList from './components/laws/LawList';
import LawDetail from './components/laws/LawDetail';
import LawEdit from './components/laws/LawEdit';
import LawVersions from './components/laws/LawVersions';
import AnnouncementList from './components/announcements/AnnouncementList';
import AnnouncementDetail from './components/announcements/AnnouncementDetail';
import AnnouncementEdit from './components/announcements/AnnouncementEdit';
import LegislativeNoticeList from './components/legislative-notices/LegislativeNoticeList';
import LegislativeNoticeDetail from './components/legislative-notices/LegislativeNoticeDetail';
import LegislativeNoticeEdit from './components/legislative-notices/LegislativeNoticeEdit';
import PublicationList from './components/publications/PublicationList';
import PublicationDetail from './components/publications/PublicationDetail';
import PublicationEdit from './components/publications/PublicationEdit';
import ManualList from './components/manuals/ManualList';
import ManualDetail from './components/manuals/ManualDetail';
import BugReportList from './components/bug-reports/BugReportList';
import UserManagement from './components/admin/UserManagement';
import EmailSender from './components/admin/EmailSender';
import EmailLogManagement from './components/admin/EmailLogManagement';
import CommentList from './components/comments/CommentList';
import ErrorBoundary from './components/common/ErrorBoundary';

// 컨텍스트
import { AuthProvider, useAuth } from './contexts/AuthContext';

// 스타일
import './App.css';

const App: React.FC = () => {
  const [i18nReady, setI18nReady] = useState(false);
  const { i18n } = useTranslation();
  
  // i18n 초기화 완료 확인
  useEffect(() => {
    const checkI18nReady = () => {
      if (i18n.isInitialized) {
        setI18nReady(true);
      } else {
        // i18n이 아직 초기화되지 않았다면 잠시 후 다시 확인
        setTimeout(checkI18nReady, 100);
      }
    };
    
    checkI18nReady();
  }, [i18n.isInitialized]);
  
  // 현재 언어에 따라 Ant Design 로케일 설정
  const antdLocale = i18n.language === 'ko' ? koKR : enUS;

  // 언어 변경 시 기존 메시지 제거
  useEffect(() => {
    if (i18nReady) {
      message.destroy();
    }
  }, [i18n.language, i18nReady]);

  // i18n이 준비되지 않았다면 로딩 화면 표시
  if (!i18nReady) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '16px'
      }}>
        번역 시스템 초기화 중...
      </div>
    );
  }

  return (
    <ConfigProvider locale={antdLocale}>
      <ErrorBoundary>
        <AuthProvider>
          <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <AppRoutes />
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </ConfigProvider>
  );
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  // 로딩 중일 때는 로딩 화면 표시
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '16px'
      }}>
        로딩 중...
      </div>
    );
  }

  return (
    <Routes>
      {/* 홈페이지 - 인증 불필요 */}
      <Route path="/" element={<Home />} />
      
      {/* 인증 관련 라우트들 - 인증 불필요 */}
      <Route path="/login" element={<LoginForm onSwitchToRegister={() => {}} />} />
      <Route path="/register" element={<RegisterForm onSwitchToLogin={() => {}} />} />
      
      {/* 읽기 전용 라우트들 - 인증 불필요 */}
      <Route path="/laws" element={<MainLayout><LawList /></MainLayout>} />
      <Route path="/laws/:id" element={<MainLayout><LawDetail /></MainLayout>} />
      <Route path="/laws/:id/versions" element={<MainLayout><LawVersions /></MainLayout>} />
      <Route path="/announcements" element={<MainLayout><AnnouncementList /></MainLayout>} />
      <Route path="/announcements/:id" element={<MainLayout><AnnouncementDetail /></MainLayout>} />
      <Route path="/legislative-notices" element={<MainLayout><LegislativeNoticeList /></MainLayout>} />
      <Route path="/legislative-notices/:id" element={<MainLayout><LegislativeNoticeDetail /></MainLayout>} />
      <Route path="/publications" element={<MainLayout><PublicationList /></MainLayout>} />
      <Route path="/publications/:id" element={<MainLayout><PublicationDetail /></MainLayout>} />
      <Route path="/manuals" element={<MainLayout><ManualList /></MainLayout>} />
      <Route path="/manuals/:id" element={<MainLayout><ManualDetail /></MainLayout>} />
      
      {/* 인증이 필요한 라우트들 */}
      {isAuthenticated && (
        <>
          <Route path="/profile" element={<MainLayout><Profile /></MainLayout>} />
          <Route path="/laws/create" element={<MainLayout><LawEdit /></MainLayout>} />
          <Route path="/laws/:id/edit" element={<MainLayout><LawEdit /></MainLayout>} />
          <Route path="/announcements/create" element={<MainLayout><AnnouncementEdit /></MainLayout>} />
          <Route path="/announcements/:id/edit" element={<MainLayout><AnnouncementEdit /></MainLayout>} />
          <Route path="/legislative-notices/create" element={<MainLayout><LegislativeNoticeEdit /></MainLayout>} />
          <Route path="/legislative-notices/:id/edit" element={<MainLayout><LegislativeNoticeEdit /></MainLayout>} />
          <Route path="/publications/create" element={<MainLayout><PublicationEdit /></MainLayout>} />
          <Route path="/publications/:id/edit" element={<MainLayout><PublicationEdit /></MainLayout>} />
          <Route path="/bug-reports" element={<MainLayout><BugReportList /></MainLayout>} />
          <Route path="/comments" element={<MainLayout><CommentList /></MainLayout>} />
          
          {/* 관리자 전용 라우트들 */}
          {isAdmin && (
            <>
              <Route path="/admin/users" element={<MainLayout><UserManagement /></MainLayout>} />
              <Route path="/admin/email" element={<MainLayout><EmailSender /></MainLayout>} />
              <Route path="/admin/email-logs" element={<MainLayout><EmailLogManagement /></MainLayout>} />
            </>
          )}
        </>
      )}
      
      {/* 404 페이지 */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default App;
